// What an option's table looks like, shared by the two ways it is shown.
//
// An option is read as a list and written as a form, and pressing Modify swaps
// one for the other in place. That only holds together if both are laid out on
// the same grid: with a column list per file, the columns would shift under the
// cursor on the way into edit mode, and drift apart over time besides.

import React from "react";
import { dropdownTriggerClasses } from "../../../components/ui";
import { MACRO_COLORS } from "../macroColors";
import type { Macros } from "../types/plans.types";

/** The macro columns, in the order the builder reads them. */
export const MACRO_COLUMNS: Array<{ key: keyof Macros; label: string }> = [
  { key: "calories", label: "Cal." },
  { key: "carbs", label: "Carb" },
  { key: "protein", label: "Prot." },
  { key: "fat", label: "Fat" },
];

/** Name, unit, quantity, the four macros, and the trailing action column. */
export const OPTION_COLUMN_COUNT = 3 + MACRO_COLUMNS.length + 1;

/**
 * The card an option is drawn on, whether it is being read or written — the
 * same one either way, so pressing Modify changes what is in the card and not
 * the card itself. The controls appearing in it is difference enough.
 */
export const optionCardClasses =
  "rounded-md border-[0.5px] border-[#E2E2E2] bg-[#FCFCFD]";

/**
 * The hairline inside that card: under the headings, and above the totals. It
 * matches the card's own border rather than the app's `line`, so the rules on
 * an option all read as one weight.
 */
export const optionRuleClasses = "border-[#E2E2E2]";

/**
 * Narrower below `3xl` to match the smaller controls edit mode uses there —
 * the point of shrinking them is that the eight columns fit without scrolling.
 */
export const optionTableClasses =
  "w-full min-w-[720px] 3xl:min-w-[820px] table-fixed border-collapse";

/**
 * The one place the column widths are written. The last column carries the
 * row's delete while editing and nothing while reading, but it is kept in both
 * so the macros land in the same place either way.
 */
export const OptionColgroup: React.FC = () => (
  <colgroup>
    <col className="w-[25%]" />
    <col className="w-[20%]" />
    <col className="w-[21%]" />
    <col className="w-[8%]" />
    <col className="w-[8%]" />
    <col className="w-[8%]" />
    <col className="w-[6%]" />
    <col className="w-[4%]" />
  </colgroup>
);

/**
 * The box a typed cell wears — the quantity, the one thing in a row that is
 * written rather than picked.
 *
 * It is the dropdown's own small trigger, so the input is exactly as tall as
 * the selects either side of it and stays that way if that control is ever
 * resized. A hand-copied set of paddings here is how a row quietly stops
 * sitting on one line.
 */
export const optionInputClasses = `${dropdownTriggerClasses(
  false,
  false,
  "sm"
)} text-ink placeholder:text-ink-subtle`;

// A size down until `3xl`, where the row finally has the width for a
// full-sized control.
export const optionHeadCell =
  "pb-2 pr-3 3xl:pb-3 3xl:pr-4 text-left text-[12px] 3xl:text-[13px] font-normal text-ink-muted";
export const optionCell = "pb-2 pr-3 3xl:pb-3 3xl:pr-4 align-middle";
export const optionTextCell = `${optionCell} text-[13px] 3xl:text-[14px] text-ink`;

/** A macro column's heading: its colour chip, then its short name. */
export const MacroHeading: React.FC<{ macro: keyof Macros; label: string }> = ({
  macro,
  label,
}) => (
  <span className="inline-flex items-center gap-1.5">
    <span
      aria-hidden="true"
      className="h-3 w-[3px] rounded-full"
      style={{ backgroundColor: MACRO_COLORS[macro] }}
    />
    {label}
  </span>
);
